import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import {
  Wifi, Tv, Globe, Server, Code, Phone, Network,
  BookOpen, Cable, Cpu, ArrowRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Wifi, Tv, Globe, Server, Code, Phone, Network,
  BookOpen, Cable, Cpu, Container: Globe,
};

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data.services);
      } catch (e) {
        console.error("Failed to fetch services", e);
        // fallback static data
        setServices([
          { id: "isp", title: "Internet Service Provider", description: "Pan-India ISP operations serving nearly a million subscribers with high-speed fiber broadband.", icon: "Wifi" },
          { id: "iptv", title: "IPTV MSO Operations", description: "Licensed Multi-System Operator for IPTV with 100,000+ subscribers across India.", icon: "Tv" },
          { id: "telecom-trade", title: "Telecom Equipment Import & Export", description: "International trading of telecommunications products including OLTs, ONTs, and set-top boxes.", icon: "Globe" },
          { id: "system-integrator", title: "System Integration", description: "End-to-end system integration for telecom networks and enterprise IT infrastructure.", icon: "Cpu" },
          { id: "ivr", title: "IVR Solutions", description: "Interactive Voice Response systems for offices, businesses, and schools.", icon: "Phone" },
          { id: "hosting", title: "Website Hosting Services", description: "Enterprise-grade web hosting with 99.9% uptime SLA.", icon: "Server" },
          { id: "web-dev", title: "Website & App Development", description: "Full-stack web and mobile application development.", icon: "Code" },
          { id: "ip1", title: "IP-1 Fiber Leasing", description: "Licensed IP-1 provider leasing dark fiber and lit fiber capacity.", icon: "Cable" },
          { id: "mpls", title: "MPLS Connectivity", description: "Multi-Protocol Label Switching for secure WAN connectivity.", icon: "Network" },
          { id: "isp-consulting", title: "ISP Consulting", description: "Expert consulting for ISP licensing, network design, and compliance.", icon: "BookOpen" },
        ]);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section data-testid="services-hero" className="relative py-24 md:py-32 bg-[#0A192F] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-80 h-80 bg-[#EA580C] rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Our Services
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white font-['Outfit'] leading-[1.1] mb-6">
            Comprehensive Telecom
            <br />
            <span className="text-[#F97316]">& Digital Solutions</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
            From last-mile broadband to enterprise MPLS networks, we offer end-to-end
            services spanning the entire telecom and digital value chain.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => {
              const Icon = iconMap[svc.icon] || Globe;
              return (
                <Card
                  key={svc.id}
                  data-testid={`service-detail-${svc.id}`}
                  className="service-card-accent group p-8 bg-white border border-[#E2E8F0] shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 rounded-none"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#0A192F] rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-[#EA580C] transition-colors duration-300">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <Badge className="mt-2 bg-[#F1F5F9] text-[#64748B] text-[10px] uppercase tracking-wider font-medium hover:bg-[#F1F5F9]">
                      {svc.id.replace(/-/g, " ")}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A192F] font-['Outfit'] mb-3">
                    {svc.title}
                  </h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    {svc.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-us" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-8">
                Built for Scale,
                <br />Designed for Reliability
              </h2>
              <div className="space-y-6">
                {[
                  { title: "Pan-India Network", desc: "Infrastructure spanning major metros and tier-2 cities with redundant fiber routes." },
                  { title: "Licensed Operations", desc: "DOT ISP, TRAI MSO, and IP-1 licenses ensuring full regulatory compliance." },
                  { title: "24/7 NOC Support", desc: "Round-the-clock Network Operations Center monitoring and incident response." },
                  { title: "Scalable Architecture", desc: "Networks engineered to grow with your business from 10 users to 10,000." },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-2 h-2 bg-[#EA580C] rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="text-base font-semibold text-[#0A192F] font-['Outfit'] mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-[#64748B]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4373997/pexels-photo-4373997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Network infrastructure"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#EA580C] p-6">
                <div className="text-3xl font-bold text-white font-['Outfit']">1M+</div>
                <div className="text-xs uppercase tracking-wider text-white/80">Active Users</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A192F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white font-['Outfit'] mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            Our team of experts can design a tailored telecom or digital solution for your specific requirements.
          </p>
          <Link to="/contact">
            <Button
              data-testid="services-cta-btn"
              className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm px-8 py-6 text-sm font-semibold"
            >
              Request a Consultation <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
